import { $t } from '@/i18n';
/**
 * 数据格式化 Hook
 *
 * 提供 YAML/JSON 转换和特殊字段编解码功能
 * 主要用于分支合并时的数据格式处理
 */
import { cloneDeep } from 'lodash';
import { jsonToYaml, yamlToJson } from '@/utils/merge/jsonToYaml';
import { ENCODE_DECODE_FIELDS } from '../constants';
/**
 * 数据格式化 Hook
 *
 * @description
 * 封装了分支合并过程中需要的数据格式转换逻辑：
 * 1. JSON 对象转 YAML 字符串（用于编辑器显示）
 * 2. YAML 字符串转 JSON 对象（用于提交到后端）
 * 3. 特殊字段（headScript, bodyScript）的编解码处理
 *
 * @example
 * ```typescript
 * const { formatToYAML, formatToJSON, validateYAML } = useDataFormatter();
 *
 * // 转换为 YAML 用于显示
 * const yamlStr = formatToYAML(jsonData);
 *
 * // 转换回 JSON 用于提交
 * const jsonData = formatToJSON(yamlStr);
 * ```
 */
export function useDataFormatter() {
    /**
     * 解码特殊字段
     * 对 advanceConfig 中的 headScript 和 bodyScript 进行 URL 解码
     *
     * @param data - 原始数据对象
     * @returns 解码后的数据对象（深拷贝）
     */
    const decodeSpecialFields = (data) => {
        const obj = cloneDeep(data);
        ENCODE_DECODE_FIELDS.forEach((key) => {
            if (obj?.advanceConfig?.[key]) {
                try {
                    obj.advanceConfig[key] = decodeURIComponent(obj.advanceConfig[key]);
                }
                catch (e) {
                    // 解码失败则保持原值
                    console.warn(`[useDataFormatter] Failed to decode field: ${key}`, e);
                }
            }
        });
        return obj;
    };
    /**
     * 编码特殊字段
     * 对 advanceConfig 中的 headScript 和 bodyScript 进行 URL 编码
     *
     * @param data - 原始数据对象
     * @returns 编码后的数据对象（原地修改）
     */
    const encodeSpecialFields = (data) => {
        ENCODE_DECODE_FIELDS.forEach((key) => {
            if (data?.advanceConfig?.[key]) {
                try {
                    data.advanceConfig[key] = encodeURIComponent(data.advanceConfig[key]);
                }
                catch (e) {
                    // 编码失败则保持原值
                    console.warn(`[useDataFormatter] Failed to encode field: ${key}`, e);
                }
            }
        });
        return data;
    };
    /**
     * 将 JSON 对象转换为 YAML 字符串
     *
     * @description
     * 转换前会对特殊字段进行解码，使 YAML 内容更易读
     * 主要用于在编辑器中显示合并内容
     *
     * @param data - JSON 对象
     * @returns YAML 格式字符串
     */
    const formatToYAML = (data) => {
        const decodedData = decodeSpecialFields(data);
        return jsonToYaml(decodedData);
    };
    /**
     * 将 YAML 字符串转换为 JSON 对象
     *
     * @description
     * 转换后会对特殊字段进行编码，确保数据格式正确
     * 主要用于将编辑器内容提交到后端
     *
     * @param yamlStr - YAML 格式字符串
     * @returns JSON 对象
     * @throws 如果 YAML 格式无效则抛出异常
     */
    const formatToJSON = (yamlStr) => {
        const obj = yamlToJson(yamlStr);
        return encodeSpecialFields(obj);
    };
    /**
     * 校验 YAML 格式是否有效
     *
     * @param yamlStr - YAML 格式字符串
     * @returns 校验结果对象
     */
    const validateYAML = (yamlStr) => {
        try {
            yamlToJson(yamlStr);
            return { valid: true };
        }
        catch (err) {
            const result = {
                valid: false,
                error: err.message || $t('k_5d2b94'),
            };
            // 提取错误位置信息（js-yaml 的 YAMLException）
            if (err?.mark) {
                result.line = err.mark.line + 1;
                result.column = err.mark.column + 1;
            }
            return result;
        }
    };
    /**
     * 校验 JSON 格式是否有效
     *
     * @param jsonStr - JSON 格式字符串
     * @returns 校验结果对象
     */
    const validateJSON = (jsonStr) => {
        try {
            JSON.parse(jsonStr);
            return { valid: true };
        }
        catch (err) {
            const result = {
                valid: false,
                error: err.message || $t('k_58c7fc17'),
            };
            // 尝试从错误信息中提取位置
            const posMatch = err.message?.match(/position\s+(\d+)/i);
            if (posMatch) {
                const position = parseInt(posMatch[1], 10);
                const lines = jsonStr.substring(0, position).split('\n');
                result.line = lines.length;
                result.column = lines[lines.length - 1].length + 1;
            }
            return result;
        }
    };
    /**
     * 格式化 diff3 合并结果
     *
     * @description
     * diff3 合并可能返回数组（多行内容），需要格式化为字符串
     *
     * @param data - 合并结果（可能是数组或字符串）
     * @returns 格式化后的字符串
     */
    const formatDiff3Result = (data) => {
        if (Array.isArray(data)) {
            return data.join('\n');
        }
        return data;
    };
    return {
        formatToYAML,
        formatToJSON,
        validateYAML,
        validateJSON,
        formatDiff3Result,
        decodeSpecialFields,
        encodeSpecialFields,
    };
}
export default useDataFormatter;
